// ─── HADIAH PEMBELIAN — penempatan di bawah baris syaratnya (helper MURNI) ───
//
// Putusan pemilik 2026-10-28 (RENCANA-hadiah-pembelian.md §15.8): hadiah tidak
// lagi tampil sebagai kartu sendiri, melainkan menempel di bawah baris varian
// syarat yang memberinya — di keranjang belanja, Keranjang Pesanan pembeli,
// keranjang admin, dan Kasir. Aturan penempatan (satu untuk semuanya):
//
//   • tuan rumah = baris syarat HIDUP TERAKHIR pembelian itu di KARTU PERTAMA
//     (urutan tampil) yang memuat syaratnya — syarat lintas produk menempel di
//     kartu produk syarat pertama;
//   • tak ada baris syarat hidup yang tampil di tempat yang sama (semua syarat
//     batal — H7, atau ada di tab/bagian lain) → hadiah tetap di kartunya
//     sendiri, tak pernah hilang dari layar;
//   • kartu yang isinya hanya hadiah yang dipindah tak tampil lagi (kartu
//     yang kosong sejak awal — pesanan tamu tanpa baris remote — tetap).
//
// Penempatan hanya soal TAMPILAN: pesanan, tagihan, dan pengiriman tetap per
// produk. Baris yang dipindah tetap baris pesanan yang sama (id, aksi, centang).

import { isVariantlessItem } from "@/lib/orderCart";

/** Baris pesanan tersimpan yang bisa membawa jejak hadiah (mig 8680). */
export type GiftNestItem = {
  itemId: string;
  orderGiftId?: string | null;
  giftRole?: string | null;
  /** Baris batal tak pernah jadi tuan rumah (tetap boleh ikut dipindah sebagai hadiah). */
  cancelled?: boolean;
};

export type GiftNestCard<T> = { key: string; items: readonly T[] };

export type NestedGiftCard<T> = {
  key: string;
  /** Baris milik kartu ini, tanpa baris hadiah yang dipindah ke bawah syaratnya. */
  items: T[];
  /** itemId baris syarat (milik `items`) → baris hadiah yang tampil tepat sesudahnya. */
  giftsAfter: Map<string, T[]>;
};

/**
 * Pindahkan baris hadiah ke bawah baris syarat tuan rumahnya. `cards` dalam
 * urutan tampil, `items` tiap kartu juga. Kartu yang kosong sesudah pemindahan
 * dibuang; urutan kartu & baris lain dipertahankan.
 */
export function nestGiftRows<T extends GiftNestItem>(
  cards: readonly GiftNestCard<T>[],
  /** Baris syarat yang boleh jadi tuan rumah (bawaan: tak batal). */
  isLive: (item: T) => boolean = (item) => !item.cancelled
): NestedGiftCard<T>[] {
  const host = new Map<string, { card: number; itemId: string }>();
  cards.forEach((card, ci) => {
    for (const it of card.items) {
      if (it.giftRole !== "syarat" || !it.orderGiftId || !isLive(it)) continue;
      const h = host.get(it.orderGiftId);
      if (!h) host.set(it.orderGiftId, { card: ci, itemId: it.itemId });
      else if (h.card === ci) h.itemId = it.itemId;
    }
  });

  const out: NestedGiftCard<T>[] = cards.map((c) => ({ key: c.key, items: [], giftsAfter: new Map() }));
  cards.forEach((card, ci) => {
    for (const it of card.items) {
      const h = it.giftRole === "hadiah" && it.orderGiftId ? host.get(it.orderGiftId) : undefined;
      if (!h) {
        out[ci].items.push(it);
        continue;
      }
      const list = out[h.card].giftsAfter.get(h.itemId);
      if (list) list.push(it);
      else out[h.card].giftsAfter.set(h.itemId, [it]);
    }
  });
  // Kartu yang memang kosong sejak awal (pesanan tamu tanpa baris remote) tetap.
  return out.filter((c, ci) => c.items.length > 0 || cards[ci].items.length === 0);
}

/** Semua baris yang tampil di kartu bersarang: baris sendiri + hadiah yang menempel. */
export function nestedCardItems<T extends GiftNestItem>(card: {
  items: readonly T[];
  giftsAfter: ReadonlyMap<string, readonly T[]>;
}): T[] {
  const out: T[] = [];
  for (const it of card.items) {
    out.push(it);
    const after = card.giftsAfter.get(it.itemId);
    if (after) out.push(...after);
  }
  return out;
}

/**
 * Label baris hadiah yang menempel di bawah baris syarat: nama produk ikut
 * ditulis (hadiahnya bisa produk lain); varian "Standar" → nama produk saja.
 */
export function nestedGiftRowLabel(item: { productName: string; variantName: string | null | undefined }): string {
  return isVariantlessItem(item.variantName) || !item.variantName
    ? item.productName
    : `${item.productName} · ${item.variantName}`;
}

// ─── Centang terikat (revisi pemilik 2026-10-29, H46) ────────────────────────
//
// Baris hadiah yang menempel di bawah syaratnya tak punya centang sendiri: ia
// ikut terpilih bersama barang syaratnya, sehingga Buat Tagihan, Request Kirim,
// dan Cancel (H47) membawanya. Hadiah yang syaratnya tak tampil di tempat yang
// sama (H49) tetap bercentang sendiri.

/**
 * Id baris hadiah yang TERIKAT di `rows` (satu tab keranjang): pembeliannya
 * punya baris syarat hidup di `rows` — kriteria yang sama dengan `nestGiftRows`.
 */
export function boundGiftIds<T extends GiftNestItem>(
  rows: readonly T[],
  isLive: (item: T) => boolean = (item) => !item.cancelled
): Set<string> {
  const hosted = new Set<string>();
  for (const r of rows) if (r.giftRole === "syarat" && r.orderGiftId && isLive(r)) hosted.add(r.orderGiftId);
  const out = new Set<string>();
  for (const r of rows) if (r.giftRole === "hadiah" && r.orderGiftId && hosted.has(r.orderGiftId)) out.add(r.itemId);
  return out;
}

/**
 * Pilihan dirapikan: baris hadiah terikat (`bound`) tercentang bila SEMUA baris
 * syarat pembeliannya yang bisa dicentang di `rows` tercentang — sama dengan
 * aturan tagihan H27 — dan lepas begitu salah satunya dilepas. Hadiah yang tak
 * bisa dicentang (`pickable`) tak pernah ikut. Nilai pilihan hadiah yang sudah
 * ada dipertahankan; yang baru = `valueOf`. Baris lain tak disentuh.
 */
export function syncBoundGiftPicks<T extends GiftNestItem, V>(input: {
  rows: readonly T[];
  bound: ReadonlySet<string>;
  pickable: (item: T) => boolean;
  valueOf: (item: T) => V;
  selected: Readonly<Record<string, V>>;
}): Record<string, V> {
  const { rows, bound, pickable, valueOf } = input;
  const next: Record<string, V> = { ...input.selected };
  if (!bound.size) return next;
  const syaratOf = new Map<string, T[]>();
  for (const r of rows) {
    if (r.giftRole !== "syarat" || !r.orderGiftId || bound.has(r.itemId) || !pickable(r)) continue;
    const list = syaratOf.get(r.orderGiftId);
    if (list) list.push(r);
    else syaratOf.set(r.orderGiftId, [r]);
  }
  for (const g of rows) {
    if (!bound.has(g.itemId)) continue;
    const syarat = (g.orderGiftId && syaratOf.get(g.orderGiftId)) || [];
    const on = pickable(g) && syarat.length > 0 && syarat.every((s) => next[s.itemId] !== undefined);
    if (!on) delete next[g.itemId];
    else if (next[g.itemId] === undefined) next[g.itemId] = valueOf(g);
  }
  return next;
}

/** `syncBoundGiftPicks` untuk pilihan berbentuk Set (halaman Buat Tagihan / Request Kirim). */
export function syncBoundGiftSet<T extends GiftNestItem>(input: {
  rows: readonly T[];
  bound: ReadonlySet<string>;
  pickable: (item: T) => boolean;
  selected: ReadonlySet<string>;
}): Set<string> {
  const record: Record<string, true> = {};
  for (const id of input.selected) record[id] = true;
  const next = syncBoundGiftPicks({ ...input, valueOf: () => true as const, selected: record });
  return new Set(Object.keys(next));
}

/**
 * Daftar datar halaman aksi (H57) dengan baris hadiah dipindah ke bawah baris
 * syarat tuan rumahnya — aturan penempatan `nestGiftRows`. `nested` = hadiah
 * yang menempel: tanpa centang & stepper sendiri, ikut syaratnya.
 */
export function nestGiftRowsFlat<T extends GiftNestItem>(
  rows: readonly T[],
  isLive?: (item: T) => boolean
): { row: T; nested: boolean }[] {
  const out: { row: T; nested: boolean }[] = [];
  for (const card of nestGiftRows([{ key: "all", items: rows }], isLive)) {
    for (const it of card.items) {
      out.push({ row: it, nested: false });
      for (const g of card.giftsAfter.get(it.itemId) ?? []) out.push({ row: g, nested: true });
    }
  }
  return out;
}

// ─── Keranjang yang belum jadi pesanan (keranjang belanja, Kasir) ───────────

/** Kunci baris keranjang: produk + varian (cermin `GiftCartProduct.lines`). */
export type GiftLineKey = { productId: string; variationId: string };

export function giftLineKeyOf(key: GiftLineKey): string {
  return `${key.productId}::${key.variationId}`;
}

/**
 * Tuan rumah kartu hadiah yang BELUM jadi pesanan: baris syarat terakhir aturan
 * itu di kartu produk pertama (urutan tampil) yang memuat syaratnya. `order` =
 * kartu produk dalam urutan tampil, masing-masing dengan kunci barisnya.
 * Pulang: kunci baris tuan rumah → entri yang menempel sesudahnya (urutan
 * entri dipertahankan), dan entri yang tak menemukan tuan rumah.
 */
export function giftEntryHosts<E extends { syaratKeys: readonly GiftLineKey[] }>(
  order: readonly { productId: string; lines: readonly GiftLineKey[] }[],
  entries: readonly E[]
): { after: Map<string, E[]>; unplaced: E[] } {
  const after = new Map<string, E[]>();
  const unplaced: E[] = [];
  for (const entry of entries) {
    const mine = new Set(entry.syaratKeys.map(giftLineKeyOf));
    let hostKey: string | null = null;
    for (const card of order) {
      for (const line of card.lines) {
        const k = giftLineKeyOf(line);
        if (mine.has(k)) hostKey = k;
      }
      if (hostKey) break;
    }
    if (!hostKey) {
      unplaced.push(entry);
      continue;
    }
    const list = after.get(hostKey);
    if (list) list.push(entry);
    else after.set(hostKey, [entry]);
  }
  return { after, unplaced };
}
